import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Heart, Share2, BookOpen, Gem, X } from 'lucide-react';
import { PiShareFatFill } from 'react-icons/pi';
import { motion } from 'framer-motion';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import type { RoxboxTrack } from '@/contexts/RoxboxPlayerContext';

interface TrackOptionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  track: RoxboxTrack | null;
  onSubscribe?: () => void;
}

export function TrackOptionsSheet({ open, onOpenChange, track, onSubscribe }: TrackOptionsSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: subscriptionData } = useSubscriptionStatus();
  const hasActiveSubscription = subscriptionData?.subscribed;

  // Check if track is liked
  const { data: isLiked = false } = useQuery({
    queryKey: ['liked-track', track?.id, user?.id],
    queryFn: async () => {
      if (!user || !track) return false;
      const { data } = await supabase
        .from('liked_tracks')
        .select('id')
        .eq('user_id', user.id)
        .eq('track_id', track.id)
        .maybeSingle();
      return !!data;
    },
    enabled: !!user && !!track,
  });

  // Toggle like mutation (use explicit IDs so closing/unmounting doesn't break invalidation)
  const toggleLike = useMutation({
    mutationFn: async ({
      trackId,
      userId,
      nextLiked,
    }: {
      trackId: string;
      userId: string;
      nextLiked: boolean;
    }) => {
      if (!userId) throw new Error('Not authenticated');

      if (nextLiked) {
        const { error } = await supabase
          .from('liked_tracks')
          .insert({ user_id: userId, track_id: trackId });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('liked_tracks')
          .delete()
          .eq('user_id', userId)
          .eq('track_id', trackId);
        if (error) throw error;
      }

      return { trackId, userId, nextLiked };
    },
    onMutate: ({ trackId, userId, nextLiked }) => {
      // Instant UI update even if the sheet closes
      queryClient.setQueryData(['liked-track', trackId, userId], nextLiked);
      queryClient.setQueryData(['likedTracks', userId], (prev: string[] | undefined) => {
        const current = prev ?? [];
        return nextLiked
          ? Array.from(new Set([...current, trackId]))
          : current.filter((id) => id !== trackId);
      });
    },
    onSuccess: ({ trackId, userId, nextLiked }) => {
      queryClient.invalidateQueries({ queryKey: ['liked-track', trackId, userId] });
      queryClient.invalidateQueries({ queryKey: ['likedTracks', userId] });
      queryClient.invalidateQueries({ queryKey: ['liked-tracks'] });
      toast.success(nextLiked ? 'Added to Liked Songs' : 'Removed from Liked Songs');
      hapticMedium();
    },
    onError: (_err, { trackId, userId }) => {
      queryClient.invalidateQueries({ queryKey: ['liked-track', trackId, userId] });
      queryClient.invalidateQueries({ queryKey: ['likedTracks', userId] });
      toast.error('Could not update like');
    },
  });

  const handleShare = async () => {
    hapticLight();
    onOpenChange(false);
    
    const shareUrl = `${window.location.origin}/jukebox?track=${track?.id}`;
    const shareTitle = track?.name || 'Track';
    const shareArtist = track?.artist;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `Listen to ${shareTitle}${shareArtist ? ` by ${shareArtist}` : ''} on Jukebox`,
          url: shareUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied!');
        }
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  const handleLike = () => {
    if (!user) {
      toast.error('Sign in to like tracks');
      return;
    }
    if (!track) return;

    const nextLiked = !isLiked;
    toggleLike.mutate({ trackId: track.id, userId: user.id, nextLiked });
    onOpenChange(false);
  };

  const handleGoToBook = () => {
    hapticLight();
    onOpenChange(false);
    if (track?.book_id) {
      navigate(`/?book=${track.book_id}`, { state: { from: '/jukebox' } });
    }
  };

  const handleSubscribe = () => {
    hapticLight();
    onOpenChange(false);
    onSubscribe?.();
  };

  if (!track) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="bg-zinc-900 border-0 rounded-t-3xl p-0 max-h-[85vh] z-[15000]"
      >
        {/* Drag indicator */}
        <div className="flex justify-center pt-3 pb-4">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>

        {/* Track info header */}
        <div className="flex items-center gap-4 px-5 pb-5 border-b border-white/10">
          <img 
            src={track.cover_image_url} 
            alt={track.name}
            className="w-14 h-14 rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">{track.name}</p>
            <p className="text-white/60 text-sm truncate">
              {track.artist || 'Unknown Artist'}
            </p>
          </div>
        </div>

        {/* Menu options */}
        <div className="py-2">
          {/* Share */}
          <button 
            onClick={handleShare}
            className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
          >
            <PiShareFatFill className="w-6 h-6 text-white/70" />
            <span className="text-white text-base">Share</span>
          </button>

          {/* Like */}
          <button 
            onClick={handleLike}
            className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
          >
            <Heart 
              className={isLiked ? 'w-6 h-6 text-primary' : 'w-6 h-6 text-white/70'}
              fill={isLiked ? 'currentColor' : 'none'}
            />
            <span className="text-white text-base">
              {isLiked ? 'Remove from Liked Songs' : 'Add to Liked Songs'}
            </span>
          </button>

          {/* Go to Audiobook - only if linked */}
          {track.book_id && (
            <button 
              onClick={handleGoToBook}
              className="w-full flex items-center gap-4 px-5 py-4 active:bg-white/5 transition-colors"
            >
              <BookOpen className="w-6 h-6 text-white/70" />
              <span className="text-white text-base">Go to Audiobook</span>
            </button>
          )}

          {/* Subscribe - only if not subscribed */}
          {!hasActiveSubscription && (
            <button 
              onClick={handleSubscribe}
              className="w-full flex items-center justify-between px-5 py-4 active:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                <Gem className="w-6 h-6 text-white/70" />
                <span className="text-white text-base">Unlock all content</span>
              </div>
              <span className="text-emerald-400 font-semibold text-sm">Premium</span>
            </button>
          )}
        </div>

        {/* Safe area padding */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 1rem)' }} />
      </SheetContent>
    </Sheet>
  );
}
